import { DestroyRef, inject, Injectable, OnDestroy, signal, WritableSignal } from '@angular/core';
import {
  BulkDeleteEquipmentRequest,
  BulkInstallFirmwareRequest,
  CompatibleFirmwareResponse,
  EquipmentClient,
  EquipmentExportAvailableDatesResponse,
  EquipmentExportRequest,
  GetCompatibleFirmwaresResponse,
  SearchEquipmentCountResponse,
  SearchEquipmentRequest,
  SearchEquipmentResponse,
  SearchEquipmentResponsePagedList,
} from '../../../../core/openapi/bff-admin-api';
import {
  BulkFirmwareModalData,
  BulkFirmwareModalResult,
} from './components/bulk-firmware-modal/bulk-firmware-modal.model';
import {
  auditTime,
  BehaviorSubject,
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  merge,
  Observable,
  of,
  Subject,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DeviceListMapper } from './device-list.mapper';
import { IOptionItem, ModalService, ToastService } from '@ui-lib/components';
import { prepareRequest } from '../../../../shared/utils/prepare-request.function';
import { DeviceListSorting, IDevice } from './device-list.model';
import { TelemetrySignalRService } from '../../../../core/services/telemetry/telemetry-signalr.service';
import { EquipmentInvalidatedNotification } from '../../../../core/models/telemetry-signalr.model';
import { outputError } from '../../../../shared/utils/output-error.function';
import { TranslateService } from '@ngx-translate/core';
import { LoadingState } from '../../../../shared/types/loading.type';
import { resolveExportFileName } from '../../../../shared/utils/file-name.function';
import { MIME_TYPES } from '../../../../shared/const/mime-types';
import { downloadBlob } from '../../../../shared/utils/download.function';
import { HttpResponse } from '@angular/common/http';
import { IEquipmentModeAvailability } from './components/device-card/pages/device-management/device-management.model';
import { DEFAULT_TABLE_PAGE_SIZE } from '../../../../shared/const/pagination.constants';
import { toExportDayBounds, toReportDate } from '../../../../shared/utils/report-date.function';
import { ExportDayModalComponent } from '../../../../shared/components/export-day-modal/export-day-modal.component';
import { IExportDayModalData } from '../../../../shared/components/export-day-modal/export-day-modal.model';

export const DevicesSortExpressionParameter: DeviceListSorting = {
  direction: 0,
  propertyName: 'ipAddress',
};

/** Поля запроса выдачи, не влияющие на состав выборки: счётчик и применение по фильтру их не ждут. */
const PAGINATION_REQUEST_KEYS: readonly (keyof SearchEquipmentRequest)[] = [
  'skip',
  'take',
  'includeTotalCount',
  'sortExpressions',
];

const REALTIME_INVALIDATION_AUDIT_MS = 2000;
const EQUIPMENT_EXPORT_DAY_TITLE = 'monitoring.export-day.devices-title';

@Injectable()
export class DeviceListService implements OnDestroy {
  private readonly equipmentClient = inject(EquipmentClient);
  private readonly toastService = inject(ToastService);
  private readonly telemetrySignalRService = inject(TelemetrySignalRService);
  private readonly translateService = inject(TranslateService);
  private readonly modalService = inject(ModalService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly equipmentById = new Map<string, IDevice>();
  private visibleEquipmentIds: string[] = [];
  private readonly subscribedEquipmentIds = new Set<string>();
  private readonly invalidatedEquipmentIds = new Set<string>();
  private focusedEquipment: { token: number; id: string } | null = null;
  private focusedEquipmentToken = 0;

  public readonly totalDeviceCount = signal(0);

  public readonly selectedDeviceIds = new Set<string>();

  /**
   * Выбраны все аппараты, попадающие под текущий фильтр, а не только строки открытой страницы.
   * В этом режиме массовое редактирование уходит в `bulk-update-by-filter`.
   */
  public readonly isAllByFilterSelected = signal(false);

  /** Количество аппаратов под текущим фильтром. `null` — ещё не посчитано или счётчик не ответил. */
  public readonly filterMatchedCount = signal<number | null>(null);

  private readonly _devicesLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _equipmentDeleteLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);
  private readonly _bulkFirmwareOptionsLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);
  private readonly _bulkFirmwareInstallLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);
  private readonly _exportLoadingStatus: WritableSignal<LoadingState> = signal<LoadingState>(
    LoadingState.IDLE,
  );
  private readonly _filterMatchedCountLoadingStatus: WritableSignal<LoadingState> =
    signal<LoadingState>(LoadingState.IDLE);

  public readonly devicesLoadingStatus = this._devicesLoadingStatus.asReadonly();
  public readonly equipmentDeleteLoadingStatus = this._equipmentDeleteLoadingStatus.asReadonly();
  public readonly bulkFirmwareOptionsLoadingStatus =
    this._bulkFirmwareOptionsLoadingStatus.asReadonly();
  public readonly bulkFirmwareInstallLoadingStatus =
    this._bulkFirmwareInstallLoadingStatus.asReadonly();
  public readonly exportLoadingStatus = this._exportLoadingStatus.asReadonly();
  public readonly filterMatchedCountLoadingStatus =
    this._filterMatchedCountLoadingStatus.asReadonly();

  private readonly devicesOrigin$ = new BehaviorSubject<boolean>(true);
  private readonly devicesRequest$ = new BehaviorSubject<Partial<SearchEquipmentRequest>>({
    skip: 0,
    take: DEFAULT_TABLE_PAGE_SIZE,
    includeTotalCount: true,
    sortExpressions: [{ ...DevicesSortExpressionParameter }],
  });
  private readonly refreshTrigger$ = new Subject<boolean>();
  private readonly invalidationRefresh$ = merge(
    this.telemetrySignalRService.equipmentInvalidated$(),
    this.telemetrySignalRService.idleTimeout$().pipe(
      map(
        (): EquipmentInvalidatedNotification => ({
          equipmentIds: this.telemetrySignalRService.getActiveEquipmentIds(),
          timestamp: '',
        }),
      ),
    ),
  ).pipe(
    tap((notification) => this.accumulateInvalidatedIds(notification)),
    auditTime(REALTIME_INVALIDATION_AUDIT_MS),
    map(() => this.getVisibleInvalidatedIds()),
    filter((ids) => ids.length > 0),
    tap(() => this.invalidatedEquipmentIds.clear()),
    tap(() => this.refreshTrigger$.next(false)),
  );

  private readonly filterCountRequest$ = new Subject<SearchEquipmentRequest>();

  /**
   * Счётчик аппаратов под фильтром. `switchMap` — при смене фильтра ответ предыдущего запроса
   * уже неактуален, показывать его нельзя.
   */
  private readonly filterMatchedCount$ = this.filterCountRequest$.pipe(
    tap(() => this._filterMatchedCountLoadingStatus.set(LoadingState.LOADING)),
    switchMap((filter: SearchEquipmentRequest) =>
      this.equipmentClient.v1EquipmentSearchCountPost(filter).pipe(
        map((response: SearchEquipmentCountResponse): number | null => response.total ?? 0),
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.devices.toast.errors.filter-count-fetching-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });

          return of(null);
        }),
      ),
    ),
    tap((total: number | null): void => {
      this.filterMatchedCount.set(total);
      this._filterMatchedCountLoadingStatus.set(LoadingState.READY);
    }),
  );

  private readonly getDevices$ = merge(
    this.devicesOrigin$.pipe(
      withLatestFrom(this.devicesRequest$),
      map(([showLoader, params]) => ({ params, showLoader })),
      switchMap((request) => this.fetchDevices(request)),
    ),
    this.refreshTrigger$.pipe(
      withLatestFrom(this.devicesRequest$),
      map(([showLoader, params]) => ({ params, showLoader })),
      exhaustMap((request) => this.fetchDevices(request)),
    ),
  );

  private togglePageDevices(value: boolean, devices: IDevice[]): void {
    devices.forEach((device) => {
      this.selectedDeviceIds[value ? 'add' : 'delete'](device.id);
    });
  }

  /**
   * Включает и выключает выбор всех аппаратов под фильтром.
   * При включении сразу запрашивает счётчик — он нужен и панели действий, и подтверждению.
   */
  public toggleAllByFilter(isSelected: boolean): void {
    this.selectedDeviceIds.clear();
    this.isAllByFilterSelected.set(isSelected);

    if (!isSelected) {
      this.resetFilterMatchedCount();

      return;
    }

    this.requestFilterMatchedCount();
  }

  public toggleDevice(check: boolean, deviceId: string, pageDevices: IDevice[] = []): void {
    this.setDevicesSelected(check ? [deviceId] : [], check ? [] : [deviceId], pageDevices);
  }

  /**
   * Пакетная версия `toggleDevice`: выделение протягиванием за одно событие меняет
   * сразу несколько строк.
   */
  public setDevicesSelected(
    selectIds: string[],
    deselectIds: string[],
    pageDevices: IDevice[] = [],
  ): void {
    // Из выбора «всё по фильтру» нельзя исключить отдельный аппарат: API работает по фильтру
    // целиком. Поэтому снятие галочки в строке переводит выбор в поштучный по текущей странице.
    if (this.isAllByFilterSelected()) {
      this.isAllByFilterSelected.set(false);
      this.resetFilterMatchedCount();
      this.togglePageDevices(true, pageDevices);
    }

    selectIds.forEach((id: string): void => {
      this.selectedDeviceIds.add(id);
    });
    deselectIds.forEach((id: string): void => {
      this.selectedDeviceIds.delete(id);
    });
  }

  public clearAllSelections(): void {
    this.selectedDeviceIds.clear();
    this.isAllByFilterSelected.set(false);
    this.resetFilterMatchedCount();
  }

  public isDeviceSelected(deviceId: string): boolean {
    return this.isAllByFilterSelected() || this.selectedDeviceIds.has(deviceId);
  }

  /**
   * Фильтр текущей выдачи без пагинации и сортировки.
   * Один и тот же объект уходит и в счётчик, и в применение по фильтру — иначе числа разойдутся.
   */
  public getFilterSnapshot(): SearchEquipmentRequest {
    // Убираем только пагинацию и сортировку: любое новое поле фильтра должно попасть
    // и в счётчик, и в применение по фильтру, иначе числа разойдутся.
    const filter: Record<string, unknown> = { ...this.devicesRequest$.value };

    PAGINATION_REQUEST_KEYS.forEach((key: keyof SearchEquipmentRequest): void => {
      delete filter[key];
    });

    return filter as SearchEquipmentRequest;
  }

  public initSelectionCounter(): void {
    this.filterMatchedCount$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private requestFilterMatchedCount(): void {
    this.filterCountRequest$.next(this.getFilterSnapshot());
  }

  private resetFilterMatchedCount(): void {
    this.filterMatchedCount.set(null);
    this._filterMatchedCountLoadingStatus.set(LoadingState.IDLE);
  }

  private getFilterKey(): string {
    const filter = this.getFilterSnapshot() as Record<string, unknown>;

    return JSON.stringify(
      Object.keys(filter)
        .sort((a, b) => a.localeCompare(b))
        .map((key) => [key, filter[key]]),
    );
  }

  public readonly devices$: Observable<IDevice[]> = this.getDevices$.pipe(
    tap((devices: IDevice[]): void => {
      this.updateEquipmentCache(devices);
      this.updateVisibleEquipmentIds(devices.map((d) => d.id));
    }),
  );

  public patchDevicesRequest(params: Partial<SearchEquipmentRequest>): void {
    const previousFilterKey = this.getFilterKey();

    this.devicesRequest$.next({
      ...this.devicesRequest$.value,
      ...prepareRequest(params),
    });

    // Выбор «всё по фильтру» следует за фильтром, поэтому счётчик пересчитывается.
    // Смена страницы и сортировки состав выборки не меняет — там пересчёт не нужен.
    if (this.isAllByFilterSelected() && this.getFilterKey() !== previousFilterKey) {
      this.requestFilterMatchedCount();
    }
  }

  public reloadDevices(showLoader = true): void {
    this.devicesOrigin$.next(showLoader);
  }

  public changeSortExpression(value?: DeviceListSorting): void {
    const expression = value ?? { ...DevicesSortExpressionParameter };

    this.patchDevicesRequest({ skip: 0, sortExpressions: [expression] });
    this.reloadDevices();
  }

  public getEquipmentModeAvailability(equipmentIds: string[]): IEquipmentModeAvailability[] {
    return equipmentIds.flatMap((equipmentId: string): IEquipmentModeAvailability[] => {
      const equipment = this.equipmentById.get(equipmentId);

      return equipment
        ? [
            {
              equipmentId,
              availableModes: equipment.availableModes ?? null,
            },
          ]
        : [];
    });
  }

  public initRealtimeUpdates(): void {
    this.invalidationRefresh$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  public focusEquipment(equipmentId: string): number {
    const token = ++this.focusedEquipmentToken;
    this.focusedEquipment = { token, id: equipmentId };
    this.reconcileEquipmentSubscriptions();
    return token;
  }

  public clearFocusedEquipment(token: number): void {
    if (!this.focusedEquipment || this.focusedEquipment.token !== token) {
      return;
    }

    this.focusedEquipment = null;
    this.reconcileEquipmentSubscriptions();
  }

  public cleanupSubscriptions(): void {
    for (const id of this.subscribedEquipmentIds) {
      void this.telemetrySignalRService.unsubscribeFromEquipment(id);
    }
    this.subscribedEquipmentIds.clear();
    this.visibleEquipmentIds = [];
    this.focusedEquipment = null;
    this.invalidatedEquipmentIds.clear();
  }

  public bulkDeleteEquipment(equipmentIds: string[]): Observable<boolean> {
    const request: BulkDeleteEquipmentRequest = { equipmentIds };

    this._equipmentDeleteLoadingStatus.set(LoadingState.LOADING);
    return this.equipmentClient.v1EquipmentBulkDeleteDelete(request).pipe(
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.devices.toast.errors.equipment-delete-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._equipmentDeleteLoadingStatus.set(LoadingState.READY)),
    );
  }

  public buildBulkFirmwareModalData(
    equipmentIds: string[],
  ): Observable<BulkFirmwareModalData | null> {
    this._bulkFirmwareOptionsLoadingStatus.set(LoadingState.LOADING);
    return this.equipmentClient.v1EquipmentCompatibleFirmwaresPost({ equipmentIds }).pipe(
      map(
        (response: GetCompatibleFirmwaresResponse): BulkFirmwareModalData => ({
          equipmentIds,
          firmwareOptions: this.mapCompatibleFirmwares(response),
        }),
      ),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.devices.toast.errors.compatible-firmwares-fetching-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(null);
      }),
      finalize(() => this._bulkFirmwareOptionsLoadingStatus.set(LoadingState.READY)),
    );
  }

  public launchBulkFirmwareInstall(selection: BulkFirmwareModalResult): Observable<boolean> {
    const request: BulkInstallFirmwareRequest = {
      requestId: globalThis.crypto.randomUUID(),
      firmwareId: selection.firmwareId,
      equipmentIds: selection.equipmentIds,
    };

    this._bulkFirmwareInstallLoadingStatus.set(LoadingState.LOADING);
    return this.equipmentClient.v1EquipmentBulkInstallFirmwarePost(request).pipe(
      map(() => true),
      catchError((error) => {
        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.devices.toast.errors.bulk-firmware-launch-failed',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of(false);
      }),
      finalize(() => this._bulkFirmwareInstallLoadingStatus.set(LoadingState.READY)),
    );
  }

  public exportEquipments(): void {
    if (this._exportLoadingStatus() === LoadingState.LOADING) {
      return;
    }

    this._exportLoadingStatus.set(LoadingState.LOADING);

    this.equipmentClient
      .v1EquipmentExportAvailableDatesGet()
      .pipe(
        map((response: EquipmentExportAvailableDatesResponse) => toExportDayBounds(response)),
        catchError(() => of(null)),
        take(1),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((bounds) => {
        if (bounds === null) {
          this._exportLoadingStatus.set(LoadingState.READY);

          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.devices.toast.errors.export-failed',
              { error: this.translateService.instant('errors.unknown') },
            ),
          });

          return;
        }

        this.openExportDayModal(bounds);
      });
  }

  private openExportDayModal(bounds: Omit<IExportDayModalData, 'titleTranslateKey'>): void {
    this.modalService
      .open<IExportDayModalData, Date>({
        component: ExportDayModalComponent,
        customContainerClass: 'export-day-modal-container',
        data: {
          titleTranslateKey: EQUIPMENT_EXPORT_DAY_TITLE,
          earliestDate: bounds.earliestDate,
          latestDate: bounds.latestDate,
        },
      })
      .afterClose$.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((day) => {
        if (!(day instanceof Date)) {
          this._exportLoadingStatus.set(LoadingState.READY);

          return;
        }

        this.sendEquipmentExport(day);
      });
  }

  private sendEquipmentExport(reportDate: Date): void {
    const request: EquipmentExportRequest = {
      ...this.devicesRequest$.value,
      reportDate: toReportDate(reportDate),
    };

    this._exportLoadingStatus.set(LoadingState.LOADING);
    this.equipmentClient
      .v1EquipmentExportPost(request, 'response', false, {
        httpHeaderAccept: MIME_TYPES.EXCEL,
        responseType: 'blob',
      } as never)
      .pipe(
        filter(
          (response: HttpResponse<Blob | null>): response is HttpResponse<Blob> & { body: Blob } =>
            !!response.body,
        ),
        catchError((error) => {
          this.toastService.error({
            message: this.translateService.instant(
              'monitoring.devices.toast.errors.export-failed',
              { error: outputError(error, this.translateService.instant('errors.unknown')) },
            ),
          });

          return of();
        }),
        finalize(() => this._exportLoadingStatus.set(LoadingState.READY)),
      )
      .subscribe((response: HttpResponse<Blob> & { body: Blob }) => {
        const fileName = resolveExportFileName(response.headers, 'devices_export', 'xlsx');

        downloadBlob(response.body, fileName);

        this.toastService.success({
          message: this.translateService.instant('monitoring.devices.toast.success.export-success'),
        });
      });
  }

  private mapCompatibleFirmwares(response: GetCompatibleFirmwaresResponse): IOptionItem<string>[] {
    const options: IOptionItem<string>[] = [];

    for (const firmware of response.compatibleFirmwares ?? []) {
      const id = firmware.id?.trim() ?? '';
      if (id.length === 0) continue;

      options.push({ id, label: this.getFirmwareLabel(firmware) });
    }

    return options;
  }

  private getFirmwareLabel(firmware: CompatibleFirmwareResponse): string {
    const name = firmware.name?.trim() ?? '';
    const version = firmware.version?.trim() ?? '';
    const label = [name, version].filter((part) => part.length > 0).join(' ');

    return label.length > 0 ? label : (firmware.id ?? '');
  }

  private updateEquipmentCache(devices: IDevice[]): void {
    devices.forEach((device: IDevice): void => {
      this.equipmentById.set(device.id, device);
    });
  }

  private updateVisibleEquipmentIds(newIds: string[]): void {
    this.visibleEquipmentIds = newIds;
    this.reconcileEquipmentSubscriptions();
  }

  private reconcileEquipmentSubscriptions(): void {
    const targetIds = new Set(this.visibleEquipmentIds);
    if (this.focusedEquipment) {
      targetIds.add(this.focusedEquipment.id);
    }

    for (const id of Array.from(this.subscribedEquipmentIds)) {
      if (targetIds.has(id)) {
        continue;
      }

      this.subscribedEquipmentIds.delete(id);
      void this.telemetrySignalRService.unsubscribeFromEquipment(id);
    }

    for (const id of targetIds) {
      if (this.subscribedEquipmentIds.has(id)) {
        continue;
      }

      this.subscribedEquipmentIds.add(id);
      void this.telemetrySignalRService.subscribeToEquipment(id);
    }
  }

  private fetchDevices({
    params,
    showLoader,
  }: {
    params: Partial<SearchEquipmentRequest>;
    showLoader: boolean;
  }): Observable<IDevice[]> {
    if (showLoader) {
      this._devicesLoadingStatus.set(LoadingState.LOADING);
    }

    return this.equipmentClient.v1EquipmentSearchPost(params).pipe(
      tap(({ totalCount }: SearchEquipmentResponsePagedList) => {
        this.totalDeviceCount.set(totalCount ?? 0);
      }),
      map(
        ({ items }: SearchEquipmentResponsePagedList): IDevice[] =>
          items?.map(
            (item: SearchEquipmentResponse): IDevice => DeviceListMapper.mapFromDto(item),
          ) ?? [],
      ),
      tap(() => this._devicesLoadingStatus.set(LoadingState.READY)),
      catchError((error) => {
        if (showLoader) {
          this._devicesLoadingStatus.set(LoadingState.IDLE);
          this.totalDeviceCount.set(0);
        }

        this.toastService.error({
          message: this.translateService.instant(
            'monitoring.devices.toast.errors.device-list-fetching',
            { error: outputError(error, this.translateService.instant('errors.unknown')) },
          ),
        });

        return of([]);
      }),
    );
  }

  private accumulateInvalidatedIds(notification: EquipmentInvalidatedNotification): void {
    for (const id of notification.equipmentIds) {
      this.invalidatedEquipmentIds.add(id);
    }
  }

  private getVisibleInvalidatedIds(): string[] {
    return this.visibleEquipmentIds.filter((id) => this.invalidatedEquipmentIds.has(id));
  }

  public ngOnDestroy(): void {
    this.cleanupSubscriptions();
    this.devicesOrigin$.complete();
    this.devicesRequest$.complete();
    this.refreshTrigger$.complete();
    this.filterCountRequest$.complete();
  }
}
